var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState, useRef } from 'react';
import { styled, createGlobalStyle, ThemeProvider } from 'styled-components';
import { useOutside, useToggle } from '@hn-front/utils';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate, } from 'react-router-dom';
import globalTheme from '../../../landing/components/core/themes/theme';
import { PeriodControl } from './PeriodControl';
import { StudentList } from './StudentList';
import { TeacherSortMenu } from './TeacherSortMenu';
import { useSort } from './hooks/useSort';
import { PdfButtonWithTooltip } from '../PdfButtonWithTooltip';
import { ActivityStreamEvents, useActivityStreamAnalytics, } from './hooks/useActivityStreamAnalytics';
import { useFetchGroupsActivityStream } from './hooks/useFetchGroupsActivityStream';
import { SortDirections, SortTypes } from './constants/sort';
var GlobalStyle = createGlobalStyle(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  html, body {\n    height: 100%;\n    overflow-x: hidden !important;\n  }\n  body {\n    margin: 0;\n    padding: 0;\n    -webkit-font-smoothing: antialiased;\n    font-family: 'Noto Sans', sans-serif;\n    font-weight: 400;\n    color: ", ";\n  }\n  div, section {\n    box-sizing: border-box;\n    margin: 0;\n    padding: 0;\n  }\n  button {\n    border: none;\n    font-family: 'Noto Sans', sans-serif;\n    background: none;\n    color: ", ";\n  }\n  h1, h2, h3, p, menu {\n    margin: 0;\n    padding: 0;\n  }\n  a {\n    text-decoration: none;\n    &:hover {\n      text-decoration: none;\n    }\n  }\n"], ["\n  html, body {\n    height: 100%;\n    overflow-x: hidden !important;\n  }\n  body {\n    margin: 0;\n    padding: 0;\n    -webkit-font-smoothing: antialiased;\n    font-family: 'Noto Sans', sans-serif;\n    font-weight: 400;\n    color: ", ";\n  }\n  div, section {\n    box-sizing: border-box;\n    margin: 0;\n    padding: 0;\n  }\n  button {\n    border: none;\n    font-family: 'Noto Sans', sans-serif;\n    background: none;\n    color: ", ";\n  }\n  h1, h2, h3, p, menu {\n    margin: 0;\n    padding: 0;\n  }\n  a {\n    text-decoration: none;\n    &:hover {\n      text-decoration: none;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.blackText;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.blackText;
});
var Container = styled.div.withConfig({ displayName: "Container", componentId: "sc-whh1o6" })(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-flow: column;\n  color: ", ";\n"], ["\n  display: flex;\n  flex-flow: column;\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.blackText;
});
var ReportTitle = styled.p.withConfig({ displayName: "ReportTitle", componentId: "sc-19aj2qf" })(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  font-weight: bold;\n  font-size: 24px;\n  line-height: 32px;\n  letter-spacing: -0.2px;\n  margin: 20px 0 10px;\n  text-size-adjust: none;\n"], ["\n  font-weight: bold;\n  font-size: 24px;\n  line-height: 32px;\n  letter-spacing: -0.2px;\n  margin: 20px 0 10px;\n  text-size-adjust: none;\n"])));
var ReportInfo = styled.p.withConfig({ displayName: "ReportInfo", componentId: "sc-1y163lf" })(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  font-size: 16px;\n  line-height: 24px;\n  white-space: pre-line;\n  margin-bottom: 32px;\n  text-size-adjust: none;\n"], ["\n  font-size: 16px;\n  line-height: 24px;\n  white-space: pre-line;\n  margin-bottom: 32px;\n  text-size-adjust: none;\n"])));
var ControlsContainer = styled.div.withConfig({ displayName: "ControlsContainer", componentId: "sc-ez39c2" })(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  margin-bottom: 32px;\n"], ["\n  display: flex;\n  justify-content: space-between;\n  margin-bottom: 32px;\n"])));
var ScTeacherSortMenu = styled(TeacherSortMenu).withConfig({ displayName: "ScTeacherSortMenu", componentId: "sc-wkjflk" })(templateObject_6 || (templateObject_6 = __makeTemplateObject([""], [""])));
var ScPdfButtonWithTooltip = styled(PdfButtonWithTooltip).withConfig({ displayName: "ScPdfButtonWithTooltip", componentId: "sc-g0hcqc" })(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  background-color: ", ";\n  padding-top: 8px;\n  padding-bottom: 8px;\n"], ["\n  background-color: ", ";\n  padding-top: 8px;\n  padding-bottom: 8px;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.primary;
});
var getEventNameForSortType = function (sortType) {
    switch (sortType) {
        case SortTypes.ByStudentNameDown:
        case SortTypes.ByStudentNameUp:
            return ActivityStreamEvents.tableSortByStudentsNameClick;
        case SortTypes.ByTimeSpentDown:
        case SortTypes.ByTimeSpentUp:
            return ActivityStreamEvents.tableSortByTimeSpentClick;
        case SortTypes.ByNumberOfTasksCompletedDown:
        case SortTypes.ByNumberOfTasksCompletedUp:
            return ActivityStreamEvents.tableSortByTasksClick;
        case SortTypes.ByAccuracyDown:
        case SortTypes.ByAccuracyUp:
            return ActivityStreamEvents.tableSortByAccuracyClick;
        case SortTypes.ByNumberOfTasksStruggleDown:
        case SortTypes.ByNumberOfTasksStruggleUp:
            return ActivityStreamEvents.tableSortByStruggleTasksClick;
        default:
            return null;
    }
};
var EventsByPeriod = {
    daily: ActivityStreamEvents.rangeDailySelect,
    weekly: ActivityStreamEvents.rangeWeeklySelect,
    monthly: ActivityStreamEvents.rangeMonthlySelect,
    yearly: ActivityStreamEvents.rangeYearlySelect,
};
var ActivityStreamDashboardApp = function (_a) {
    var schoolId = _a.schoolId, groupId = _a.groupId, role = _a.role;
    var pathname = useLocation().pathname;
    var period = pathname.replace('/', '');
    var navigate = useNavigate();
    var _b = useToggle(false), isSortMenuOpen = _b.isToggled, toggleIsSortMenuOpen = _b.toggle;
    var _c = useState({
        type: SortTypes.ByStudentNameDown,
        direction: SortDirections.Desc,
    }), sortInfo = _c[0], setSortInfo = _c[1];
    var sortMenuRef = useRef(null);
    var sendGaEvent = useActivityStreamAnalytics();
    useOutside(sortMenuRef, function () {
        if (isSortMenuOpen)
            toggleIsSortMenuOpen();
    });
    var onChangeSort = useCallback(function (value) {
        var event = getEventNameForSortType(value.sortType);
        if (event)
            sendGaEvent(event);
        setSortInfo(value);
        toggleIsSortMenuOpen();
    }, [toggleIsSortMenuOpen, sendGaEvent]);
    var onDateChanged = useCallback(function (path) {
        var event = EventsByPeriod[path];
        if (event)
            sendGaEvent(event);
        navigate("/" + path);
    }, [navigate, sendGaEvent]);
    var data = useFetchGroupsActivityStream(groupId, {
        schoolId: schoolId,
        role: role,
        period: period,
    }).response;
    var sortedData = useSort(data, sortInfo);
    var reportUrl = "/reports/activity_stream/pdf?schoolId=" + schoolId + "&groupId=" + groupId + "&period=" + period.toLowerCase();
    return (_jsxs(Container, { children: [_jsx(ReportTitle, { children: "Activity Stream" }, void 0), _jsx(ReportInfo, { children: "Gain better understanding of how your class and individual students are performing on a daily/weekly/monthly basis or for the school year." }, void 0), _jsxs(ControlsContainer, { children: [_jsx(PeriodControl, { onDateChanged: onDateChanged, date: period }, void 0), _jsx(ScPdfButtonWithTooltip, __assign({ apiUrl: reportUrl, onClick: function () { return sendGaEvent(ActivityStreamEvents.downloadReportClick); } }, { children: "PDF" }), void 0)] }, void 0), _jsx(ScTeacherSortMenu, { onChange: onChangeSort }, void 0), (sortedData === null || sortedData === void 0 ? void 0 : sortedData.groups) && (_jsx(StudentList, { groups: sortedData.groups, role: role, schoolId: schoolId }, void 0))] }, void 0));
};
var ActivityStreamDashboard = function (props) {
    var app = _jsx(ActivityStreamDashboardApp, __assign({}, props), void 0);
    var baseName = window.location.pathname.split('activity_stream')[0] + "activity_stream";
    var sendGaEvent = useActivityStreamAnalytics();
    useEffect(function () {
        sendGaEvent(ActivityStreamEvents.pageView);
    }, [sendGaEvent]);
    return (_jsxs(ThemeProvider, __assign({ theme: globalTheme }, { children: [_jsx(GlobalStyle, {}, void 0), _jsx(Router, __assign({ basename: baseName }, { children: _jsxs(Routes, { children: [_jsx(Route, { path: '/daily', element: app }, void 0), _jsx(Route, { path: '/weekly', element: app }, void 0), _jsx(Route, { path: '/monthly', element: app }, void 0), _jsx(Route, { path: '/yearly', element: app }, void 0), _jsx(Route, { path: '*', element: _jsx(Navigate, { to: 'weekly' }, void 0) }, void 0)] }, void 0) }), void 0)] }), void 0));
};
export default ActivityStreamDashboard;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
